import './ProfileView.css'

import { useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { session, profile, agentChat } from '../store'

import { useRequireAuthentication } from '../hooks/useRequireAuthentication'
import { useRequireOnboarding } from '../hooks/useRequireOnboarding'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { PersonProfile } from '../components/PersonProfile'
import { unlockAudioPlayback } from '../lib/voice'

export function ProfileView() {
  useRequireAuthentication()
  useRequireOnboarding()

  const { profileId } = useParams<{ profileId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const authStatus = useAtomValue(session.sessionStatusAtom)
  const currentSession = useAtomValue(session.sessionAtom)
  const fetchedProfile = useAtomValue(profile.profileAtom)
  const isLoading = useAtomValue(profile.profileIsLoadingAtom)
  const error = useAtomValue(profile.profileErrorAtom)

  const fetchProfile = useSetAtom(profile.fetchProfileAtom)
  const fetchAttributes = useSetAtom(profile.fetchProfileAttributesAtom)
  const clearProfile = useSetAtom(profile.clearProfileAtom)
  const openChat = useSetAtom(agentChat.agentChatOpenAtom)

  const attributes = useAtomValue(profile.profileAttributesAtom)
  const [, setBreadcrumbs] = useBreadcrumbs()

  const isOwnProfile = !profileId
  const currentProfile = isOwnProfile ? currentSession?.user.profile : fetchedProfile
  const photoIds =
    (isOwnProfile ? currentProfile?.photoIds : currentProfile?.approvedPhotoIds) ?? []

  useEffect(() => {
    if (!currentProfile) return

    if (isOwnProfile) {
      setBreadcrumbs([{ label: 'Your Profile' }])
      return
    }

    const state = location.state as { selectedMatchId?: string } | null
    setBreadcrumbs([
      {
        label: 'Matches',
        path: '/matches',
        ...(state?.selectedMatchId && { state: { selectedMatchId: state.selectedMatchId } }),
      },
      { label: currentProfile.name },
    ])
  }, [currentProfile, isOwnProfile, setBreadcrumbs, location.state])

  useEffect(() => {
    if (authStatus !== 'authenticated') return

    clearProfile()

    if (isOwnProfile) {
      const ownProfileId = currentSession?.user.profile?.id
      if (ownProfileId) fetchAttributes(ownProfileId)
    } else if (profileId) {
      fetchProfile(profileId).then((next) => {
        if (next) fetchAttributes(next.id)
      })
    }
  }, [
    profileId,
    authStatus,
    isOwnProfile,
    currentSession?.user.profile?.id,
    fetchProfile,
    fetchAttributes,
    clearProfile,
  ])

  if (authStatus !== 'authenticated') {
    return (
      <div className="profile-view">
        <div className="profile-view__loading">Loading...</div>
      </div>
    )
  }

  if (!isOwnProfile && isLoading) {
    return (
      <div className="profile-view">
        <div className="profile-view__loading">Loading profile...</div>
      </div>
    )
  }

  if (!isOwnProfile && error) {
    return (
      <div className="profile-view">
        <div className="profile-view__error">{error}</div>
      </div>
    )
  }

  if (!currentProfile) {
    return (
      <div className="profile-view">
        <div className="profile-view__loading">Loading profile...</div>
      </div>
    )
  }

  return (
    <div className="profile-view">
      <PersonProfile
        variant={isOwnProfile ? 'self' : 'match'}
        name={currentProfile.name}
        {...(currentProfile.bio && { bio: currentProfile.bio })}
        {...(currentProfile.birthday && { birthday: currentProfile.birthday })}
        {...(currentProfile.location && { location: currentProfile.location })}
        photoIds={photoIds}
        attributes={attributes}
        {...(isOwnProfile && {
          onEditPhotos: () => navigate('/profile/photos'),
          onEditProfile: () => {
            unlockAudioPlayback()
            openChat({
              mode: 'profile',
              initialContext: 'The user clicked the Edit Profile button on their profile page',
            })
          },
        })}
      />
    </div>
  )
}
